import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CreditCard, FlaskConical, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { BrandLogo } from "@/components/BrandLogo";
import { fmtMoney } from "@/lib/format";

// Reemplaza a Stripe Checkout cuando el backend corre con STRIPE_SIMULADO=true: no se cobra nada.
// Al pagar, el backend genera los mismos eventos que Stripe y vuelve a la página de éxito de siempre.
export default function PagoSimulado() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id") || "";
  const [info, setInfo] = useState<any>(null);
  const [failed, setFailed] = useState("");
  const [busy, setBusy] = useState<"" | "pagado" | "rechazado">("");

  useEffect(() => {
    api.get(`/payments/simulated/${encodeURIComponent(sessionId)}`).then((r) => setInfo(r.data)).catch((e) => setFailed(apiError(e)));
  }, [sessionId]);

  const finish = async (result: "pagado" | "rechazado") => {
    setBusy(result);
    try {
      const { data } = await api.post(`/payments/simulated/${encodeURIComponent(sessionId)}/complete`, { result });
      navigate(data.redirect, { replace: true });
    } catch (e) {
      toast.error(apiError(e));
      setBusy("");
    }
  };

  return (
    <div className="min-h-screen bg-obsidian flex items-center justify-center px-6" data-testid="simulated-checkout">
      <div className="max-w-md w-full card-obsidian p-8">
        <div className="flex items-center justify-center mb-6"><BrandLogo /></div>
        <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/40 text-sm text-amber-900 flex items-start gap-2 mb-6" role="status">
          <FlaskConical className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
          <span><strong>Pago simulado.</strong> Este entorno no está conectado a Stripe: no se cobra nada ni se pide una tarjeta.</span>
        </div>
        {failed ? (
          <>
            <p className="text-sm text-red-700" role="alert">{failed}</p>
            <Button onClick={() => navigate("/portal/planes")} className="mt-6 w-full rounded-full bg-navy text-white">Volver a Mis planes</Button>
          </>
        ) : !info ? (
          <div className="flex justify-center py-6" role="status"><Loader2 className="w-6 h-6 animate-spin text-navy" aria-hidden="true" /><span className="sr-only">Cargando…</span></div>
        ) : (
          <>
            <p className="text-sm text-slate-600">Vas a contratar</p>
            <h1 className="text-2xl font-serif font-medium">{info.plan_name} · {info.plan_interval === "year" ? "Anual" : "Mensual"}</h1>
            <p className="text-3xl font-bold text-navy mt-3" data-testid="simulated-amount">{fmtMoney(info.amount)}<span className="text-sm text-slate-600 font-normal"> {info.plan_interval === "year" ? "al año" : "al mes"}</span></p>
            {info.status !== "open" ? (
              <>
                <p className="text-sm text-slate-700 mt-6">Este pago ya no está disponible.</p>
                <Button onClick={() => navigate("/portal/planes")} className="mt-4 w-full rounded-full bg-navy text-white">Volver a Mis planes</Button>
              </>
            ) : (
              <div className="mt-8 space-y-3">
                <Button onClick={() => finish("pagado")} disabled={!!busy} data-testid="simulated-pay" className="w-full h-11 rounded-full gold-gradient-bg text-white font-semibold">
                  {busy === "pagado" ? <Loader2 className="w-4 h-4 animate-spin" aria-label="Procesando" /> : <><CreditCard className="w-4 h-4 mr-1" aria-hidden="true" /> Pagar (simulado)</>}
                </Button>
                <Button variant="outline" onClick={() => finish("rechazado")} disabled={!!busy} data-testid="simulated-decline" className="w-full h-11 rounded-full border-slate-300 text-slate-700">
                  Simular pago rechazado
                </Button>
                <p className="text-xs text-slate-600 text-center">«Pagar» activa el plan como si Stripe confirmara el pago. «Rechazado» vuelve como si la persona cancelara el pago.</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
